/**
 * The reader's shelf in the current world, loaded once and shared by every
 * page under it: the book page's shelf panel, the series page's progress, the
 * quiz's save.
 *
 * Signed out, it is simply `signed-out` and nothing is requested: the
 * catalogue never asks for an account, and neither does this. Signed in, one
 * request loads the whole shelf for the world (a reader's shelf in one world
 * is at most the world's books), and every change updates it in place once
 * the server has taken it.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import type { ApiResponse } from '../../shared/api.ts'
import { isMessageKey, type MessageKey } from '../../shared/message-keys.ts'
import { isEmptyEntry, type SavedQuizResult, type ShelfEntry, type ShelfEntryInput, type ShelfResponse } from '../../shared/shelf.ts'
import { authClient } from './auth-client.ts'
import type { OwnReviewsResponse } from '../../shared/reviews.ts'

export type ShelfState =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; entries: ReadonlyMap<string, ShelfEntry>; quiz: SavedQuizResult | null }

interface Shelf {
  state: ShelfState
  /** Set one book's entry. Resolves to null when saved, or the message to show. */
  save: (work: string, entry: ShelfEntryInput) => Promise<MessageKey | null>
  /** Save the latest quiz result. Resolves to whether it was saved. */
  saveQuiz: (answers: string) => Promise<boolean>
  /**
   * A note was published as a review (#107): reflect what the server did to
   * the entry, the note cleared or the whole entry deleted.
   */
  noteMoved: (work: string, entry: 'cleared' | 'deleted') => void
}

const ShelfContext = createContext<Shelf | null>(null)

const base = (world: string) => `/api/me/worlds/${encodeURIComponent(world)}`

/** The reader's own live reviews in a world, newest first (#118). */
export async function fetchOwnReviews(world: string): Promise<OwnReviewsResponse | null> {
  const response = await fetch(`${base(world)}/reviews`, { credentials: 'same-origin' })
  if (!response.ok) return null
  const body = (await response.json()) as ApiResponse<OwnReviewsResponse>
  return body.ok ? body.data : null
}

export async function fetchShelf(world: string): Promise<ShelfResponse | null> {
  const response = await fetch(`${base(world)}/shelf`, { credentials: 'same-origin' })
  if (!response.ok) return null
  const body = (await response.json()) as ApiResponse<ShelfResponse>
  return body.ok ? body.data : null
}

async function put(path: string, payload: unknown): Promise<MessageKey | null> {
  try {
    const response = await fetch(path, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (response.ok) return null
    const body = (await response.json().catch(() => null)) as ApiResponse<unknown> | null
    const key = body !== null && !body.ok ? body.error.key : null
    return key !== null && isMessageKey(key) ? key : 'error.unexpected'
  } catch {
    return 'error.unexpected'
  }
}

export function ShelfProvider({ world, children }: { world: string; children: ReactNode }) {
  const { data: session, isPending } = authClient.useSession()
  const signedIn = session !== null && session !== undefined && session.user.emailVerified
  // Whose shelf, in which world: what was loaded is only shown for the key it was loaded for.
  const key = signedIn ? `${world}:${session.user.id}` : null
  const [loaded, setLoaded] = useState<{ key: string; state: ShelfState } | null>(null)

  useEffect(() => {
    if (key === null) return
    let live = true
    fetchShelf(world).then(
      (data) => {
        if (!live) return
        setLoaded({
          key,
          state: data === null ? { status: 'error' } : { status: 'ready', entries: new Map(data.entries.map((e) => [e.work, e])), quiz: data.quiz },
        })
      },
      () => {
        if (live) setLoaded({ key, state: { status: 'error' } })
      },
    )
    return () => {
      live = false
    }
  }, [world, key])

  const state: ShelfState = isPending
    ? { status: 'loading' }
    : key === null
      ? { status: 'signed-out' }
      : loaded?.key === key
        ? loaded.state
        : { status: 'loading' }

  /** Change the loaded shelf in place, once the server has taken the change. */
  const update = useCallback(
    (change: (current: Extract<ShelfState, { status: 'ready' }>) => ShelfState) => {
      setLoaded((current) => (current === null || current.key !== key || current.state.status !== 'ready' ? current : { key, state: change(current.state) }))
    },
    [key],
  )

  const save = useCallback(
    async (work: string, entry: ShelfEntryInput): Promise<MessageKey | null> => {
      const failure = await put(`${base(world)}/shelf/${encodeURIComponent(work)}`, entry)
      if (failure !== null) return failure
      update((current) => {
        const entries = new Map(current.entries)
        if (isEmptyEntry(entry)) entries.delete(work)
        else entries.set(work, { work, ...entry, updated_at: new Date().toISOString() })
        return { ...current, entries }
      })
      return null
    },
    [world, update],
  )

  const saveQuiz = useCallback(
    async (answers: string): Promise<boolean> => {
      if (!signedIn) return false
      const failure = await put(`${base(world)}/quiz`, { answers })
      if (failure !== null) return false
      // The server scored it; read the stored result back rather than guess it.
      const data = await fetchShelf(world)
      if (data !== null) update((current) => ({ ...current, quiz: data.quiz }))
      return true
    },
    [world, signedIn, update],
  )

  const noteMoved = useCallback(
    (work: string, entry: 'cleared' | 'deleted') => {
      update((current) => {
        const entries = new Map(current.entries)
        const existing = entries.get(work)
        if (entry === 'deleted' || existing === undefined) entries.delete(work)
        else entries.set(work, { ...existing, note: null, updated_at: new Date().toISOString() })
        return { ...current, entries }
      })
    },
    [update],
  )

  return <ShelfContext.Provider value={{ state, save, saveQuiz, noteMoved }}>{children}</ShelfContext.Provider>
}

/** The reader's shelf in the current world. Only inside a world's pages. */
export function useShelf(): Shelf {
  const shelf = useContext(ShelfContext)
  if (shelf === null) throw new Error('useShelf() outside a world')
  return shelf
}
